import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'

import * as api from '../api/naskah.api.js'
import { ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthProvider.tsx'
import { NaskahPreview } from '../components/NaskahPreview.tsx'

const KIND_LABEL: Readonly<Record<api.NaskahKind, string>> = {
  TATA_TERTIB: 'Tata Tertib',
  PAKTA_INTEGRITAS: 'Pakta Integritas',
  INSTRUKSI_PESERTA: 'Instruksi Peserta',
  INFORMED_CONSENT_PS: 'Informed Consent PS',
}

function pesan(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Tidak dapat menghubungi server'
  const detail = error.details?.map((d) => `${d.field}: ${d.message}`).join('; ')
  return detail !== undefined && detail !== '' ? detail : error.message
}

function StatusBadge({ status }: { status: api.NaskahStatus }): ReactNode {
  return <span className={`badge badge-${status.toLowerCase()}`}>{status}</span>
}

export function NaskahPage(): ReactNode {
  const [rows, setRows] = useState<readonly api.NaskahRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)

  const muat = useCallback(async () => {
    try {
      setRows(await api.listNaskah())
      setError(null)
    } catch (err) {
      setError(pesan(err))
    }
  }, [])

  useEffect(() => {
    void muat()
  }, [muat])

  if (selected !== null) {
    return (
      <NaskahDetail
        slug={selected}
        onBack={() => {
          setSelected(null)
          void muat()
        }}
      />
    )
  }

  return (
    <div>
      <h1>Naskah</h1>
      <p className="hint">
        Tata tertib, pakta integritas, dan naskah lain yang dibaca peserta dan penguji. Naskah yang
        sudah terbit tidak dapat diubah — perubahan menghasilkan versi baru.
      </p>

      <div className="page-actions">
        <TambahNaskah onDone={() => void muat()} />
      </div>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {rows === null ? (
        <p className="hint">Memuat…</p>
      ) : rows.length === 0 ? (
        <p className="dt-empty">Belum ada naskah.</p>
      ) : (
        <div className="dt">
          <table>
            <thead>
              <tr>
                <th>Naskah</th>
                <th>Jenis</th>
                <th>Berlaku</th>
                <th>Sedang berjalan</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.name}</strong>
                    <br />
                    <code>{row.slug}</code>
                  </td>
                  <td>{KIND_LABEL[row.kind]}</td>
                  <td>
                    {row.berlaku === null ? (
                      <span className="hint">belum terbit</span>
                    ) : (
                      <>v{row.berlaku.version}</>
                    )}
                  </td>
                  <td>
                    {row.berjalan === null ? (
                      <span className="hint">—</span>
                    ) : (
                      <>
                        v{row.berjalan.version} <StatusBadge status={row.berjalan.status} />
                      </>
                    )}
                  </td>
                  <td>
                    <button onClick={() => setSelected(row.slug)}>Buka</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function TambahNaskah({ onDone }: { onDone: () => void }): ReactNode {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setBusy(true)
    setError(null)
    try {
      await api.createNaskah({
        slug: String(form.get('slug') ?? '').toLowerCase(),
        kind: String(form.get('kind') ?? '') as api.NaskahKind,
        name: String(form.get('name') ?? ''),
      })
      setOpen(false)
      onDone()
    } catch (err) {
      setError(pesan(err))
    } finally {
      setBusy(false)
    }
  }

  if (!open) return <button onClick={() => setOpen(true)}>+ Naskah Baru</button>

  return (
    <form className="inline-form" onSubmit={onSubmit}>
      <input name="slug" placeholder="slug, mis. tata-tertib-2026" required />
      <input name="name" placeholder="Nama naskah" required />
      <select name="kind" required defaultValue="TATA_TERTIB">
        {api.NASKAH_KINDS.map((kind) => (
          <option key={kind} value={kind}>
            {KIND_LABEL[kind]}
          </option>
        ))}
      </select>
      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <div className="inline-form-actions">
        <button type="submit" disabled={busy}>
          Simpan
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={busy}>
          Batal
        </button>
      </div>
    </form>
  )
}

function NaskahDetail({ slug, onBack }: { slug: string; onBack: () => void }): ReactNode {
  const [detail, setDetail] = useState<api.NaskahDetail | null>(null)
  const [aktif, setAktif] = useState<api.VersionDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const muat = useCallback(
    async (versionToOpen?: number) => {
      try {
        const doc = await api.getNaskah(slug)
        setDetail(doc)
        setError(null)

        const target =
          versionToOpen ??
          doc.versions.find((v) => v.status === 'DRAF' || v.status === 'DIAJUKAN')?.version ??
          doc.versions.find((v) => v.status === 'DITERBITKAN')?.version ??
          doc.versions[0]?.version

        setAktif(target === undefined ? null : await api.getVersion(slug, target))
      } catch (err) {
        setError(pesan(err))
      }
    },
    [slug],
  )

  useEffect(() => {
    void muat()
  }, [muat])

  async function jalankan(fn: () => Promise<unknown>, bukaVersi?: number): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await muat(bukaVersi)
    } catch (err) {
      setError(pesan(err))
    } finally {
      setBusy(false)
    }
  }

  if (detail === null) {
    return (
      <div>
        <button onClick={onBack}>← Kembali</button>
        {error === null ? <p className="hint">Memuat…</p> : <p className="error">{error}</p>}
      </div>
    )
  }

  const berjalan = detail.versions.find((v) => v.status === 'DRAF' || v.status === 'DIAJUKAN')

  return (
    <div className="naskah-detail">
      <button onClick={onBack}>← Kembali</button>
      <h1>{detail.name}</h1>
      <p className="hint">
        <code>{detail.slug}</code> · {KIND_LABEL[detail.kind]}
      </p>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {berjalan === undefined && (
        <div className="page-actions">
          <button
            disabled={busy}
            onClick={() =>
              void jalankan(async () => {
                const created = await api.createDraft(slug, {})
                return created
              })
            }
          >
            + Draf Versi Baru
          </button>
          <span className="hint">Disalin dari versi terakhir sebagai titik awal.</span>
        </div>
      )}

      <div className="naskah-layout">
        <section className="naskah-versions">
          <h2>Versi</h2>
          <ul className="version-list">
            {detail.versions.map((v) => (
              <li key={v.id} className={aktif?.version === v.version ? 'aktif' : undefined}>
                <button onClick={() => void jalankan(async () => undefined, v.version)}>
                  v{v.version} <StatusBadge status={v.status} />
                </button>
                <div className="hint">
                  {v.createdBy} · {v.createdAt.slice(0, 10)}
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="naskah-editor">
          {aktif === null ? (
            <p className="hint">Belum ada versi. Buat draf pertama.</p>
          ) : (
            <VersionPanel
              slug={slug}
              version={aktif}
              busy={busy}
              onAction={(fn) => void jalankan(fn, aktif.version)}
            />
          )}
        </section>
      </div>
    </div>
  )
}

function VersionPanel({
  slug,
  version,
  busy,
  onAction,
}: {
  slug: string
  version: api.VersionDetail
  busy: boolean
  onAction: (fn: () => Promise<unknown>) => void
}): ReactNode {
  const { user } = useAuth()
  const [title, setTitle] = useState(version.title)
  const [body, setBody] = useState(version.body)
  const [changeNote, setChangeNote] = useState(version.changeNote ?? '')
  const [tab, setTab] = useState<'sunting' | 'pratinjau'>('sunting')

  // Berpindah versi berarti memuat isi lain ke dalam form; tanpa ini, isi versi
  // sebelumnya tertinggal di kotak sunting dan dapat tersimpan ke versi yang salah.
  useEffect(() => {
    setTitle(version.title)
    setBody(version.body)
    setChangeNote(version.changeNote ?? '')
    setTab('sunting')
  }, [version.id, version.title, version.body, version.changeNote])

  const editable = version.status === 'DRAF'
  const pengaju = version.submittedBy
  const sayaPengaju = pengaju !== null && pengaju === user?.username

  return (
    <div>
      <div className="version-head">
        <h2>
          Versi {version.version} <StatusBadge status={version.status} />
        </h2>
        <code className="hint" title="sidik isi">
          {version.contentHash.slice(0, 16)}…
        </code>
      </div>

      {version.status === 'DIAJUKAN' && (
        <p className="notice">
          Diajukan oleh <strong>{pengaju}</strong>
          {version.submittedAt === null ? '' : ` pada ${version.submittedAt.slice(0, 10)}`}.
          {sayaPengaju
            ? ' Keputusan harus datang dari panitia pusat lain; Anda hanya dapat menariknya.'
            : ' Anda dapat menyetujui atau menolaknya.'}
        </p>
      )}
      {version.status === 'DITOLAK' && version.decisionNote !== null && (
        <p className="notice">
          Ditolak oleh <strong>{version.decidedBy}</strong>: {version.decisionNote}
        </p>
      )}

      <div className="tabs">
        <button
          className={tab === 'sunting' ? 'aktif' : undefined}
          onClick={() => setTab('sunting')}
        >
          {editable ? 'Sunting' : 'Sumber'}
        </button>
        <button
          className={tab === 'pratinjau' ? 'aktif' : undefined}
          onClick={() => setTab('pratinjau')}
        >
          Pratinjau
        </button>
      </div>

      {tab === 'pratinjau' ? (
        <NaskahPreview title={title} body={body} />
      ) : (
        <div className="naskah-form">
          <label>
            Judul
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!editable || busy}
            />
          </label>
          <label>
            Isi (Markdown — HTML tidak diizinkan)
            <textarea
              value={body}
              rows={22}
              onChange={(e) => setBody(e.target.value)}
              disabled={!editable || busy}
            />
          </label>
          <label>
            Ringkasan perubahan
            <input
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              disabled={!editable || busy}
              placeholder="Dibaca penyetuju sebelum memutuskan"
            />
          </label>
        </div>
      )}

      <div className="page-actions">
        {editable && (
          <>
            <button
              disabled={busy}
              onClick={() =>
                onAction(() => api.saveDraft(slug, version.version, { title, body, changeNote }))
              }
            >
              Simpan Draf
            </button>
            <button
              disabled={busy}
              onClick={() =>
                onAction(async () => {
                  // Simpan dulu, baru ajukan: mengajukan isi yang belum
                  // tersimpan berarti penyetuju membaca versi yang berbeda dari
                  // yang dilihat penyusun.
                  await api.saveDraft(slug, version.version, { title, body, changeNote })
                  return api.act(slug, version.version, 'ajukan')
                })
              }
            >
              Ajukan untuk Persetujuan
            </button>
          </>
        )}

        {version.status === 'DIAJUKAN' && sayaPengaju && (
          <button
            disabled={busy}
            onClick={() => onAction(() => api.act(slug, version.version, 'tarik'))}
          >
            Tarik Ajuan
          </button>
        )}

        {version.status === 'DIAJUKAN' && !sayaPengaju && (
          <>
            <button
              disabled={busy}
              onClick={() => onAction(() => api.act(slug, version.version, 'setujui'))}
            >
              Setujui dan Terbitkan
            </button>
            <Tolak
              disabled={busy}
              onTolak={(alasan) =>
                onAction(() => api.act(slug, version.version, 'tolak', { decisionNote: alasan }))
              }
            />
          </>
        )}
      </div>
    </div>
  )
}

function Tolak({
  disabled,
  onTolak,
}: {
  disabled: boolean
  onTolak: (alasan: string) => void
}): ReactNode {
  const [open, setOpen] = useState(false)
  const [alasan, setAlasan] = useState('')

  if (!open) {
    return (
      <button disabled={disabled} onClick={() => setOpen(true)}>
        Tolak
      </button>
    )
  }

  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault()
        onTolak(alasan)
        setOpen(false)
        setAlasan('')
      }}
    >
      <input
        value={alasan}
        onChange={(e) => setAlasan(e.target.value)}
        placeholder="Alasan penolakan — wajib"
        required
      />
      <div className="inline-form-actions">
        <button type="submit" disabled={disabled}>
          Tolak Naskah
        </button>
        <button type="button" onClick={() => setOpen(false)}>
          Batal
        </button>
      </div>
    </form>
  )
}
